/**
 * Phase 5 — Architect Profile page
 * Route: /architect/profile
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCog, Save, Send } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTIES = [
  'licensed_investment_professional', 'registered_broker', 'investment_adviser',
  'portfolio_manager', 'compliance_reviewer', 'business_strategist',
  'brand_advisor', 'offer_consultant', 'market_specialist',
  'general_reviewer', 'other',
];
const LICENSE_STATUS = [
  'not_applicable', 'self_reported', 'pending_verification',
  'verified', 'rejected', 'expired', 'unknown',
];
const AVAILABILITY = ['available', 'limited', 'unavailable'];
const APPROVAL_META = {
  draft: theme.colors.text.muted, submitted: theme.colors.status.info,
  under_review: theme.colors.status.warning, approved: theme.colors.status.success,
  rejected: theme.colors.status.error, suspended: theme.colors.status.error,
  archived: theme.colors.text.muted,
};

function StatusPill({ status }) {
  if (!status) return null;
  const c = APPROVAL_META[status] || theme.colors.text.muted;
  return (
    <span data-testid={`architect-approval-${status}`}
      style={{ padding: '3px 10px', borderRadius: 999, background: `${c}1A`,
        color: c, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const EMPTY = {
  full_name: '', phone: '', professional_role: '', company_or_firm: '',
  specialties: [], license_status: 'self_reported', license_details: '',
  credentials_summary: '', website_or_profile_link: '', experience_summary: '',
  availability_status: 'available',
};

export default function ArchitectProfilePage({ user }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/architect/profile`, auth);
      const p = res.data?.architect;
      setProfile(p);
      if (p) setForm({ ...EMPTY, ...p, specialties: p.specialties || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load profile');
    } finally { setLoading(false); }
  };

  const toggleSpecialty = (s) => {
    setForm(f => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter(x => x !== s)
        : [...f.specialties, s],
    }));
  };

  const save = async (submit) => {
    if (!form.full_name.trim()) { toast.error('Full Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, submit };
      const res = await axios.post(`${API}/architect/profile`, payload, auth);
      toast.success(submit ? 'Profile submitted for review' : 'Profile saved');
      setProfile(res.data?.architect);
      setForm({ ...EMPTY, ...res.data?.architect, specialties: res.data?.architect?.specialties || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="architect-profile-page" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <UserCog size={22} color={theme.colors.primary} />
        <div style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary }}>Architect Profile</div>
        {profile?.approval_status && <StatusPill status={profile.approval_status} />}
      </div>
      <div style={{ color: theme.colors.text.muted, marginBottom: 12, fontSize: 13, maxWidth: 720 }}>
        Create and manage your Kickback Architect profile for review and approval.
      </div>

      <div style={{ padding: 10, background: '#FFF7ED', border: '1px solid #FED7AA',
        borderRadius: theme.radius.sm, marginBottom: 16, fontSize: 12, color: '#7C2D12' }}>
        Kickback Architects may support eligible programs with review, structure guidance, specialty
        input, or professional support. Kickback Contract does not guarantee approval, availability,
        financial outcomes, investment performance, payouts, or legal compliance. Regulated
        activities may require properly licensed professionals and separate agreements.
      </div>

      {profile?.rejection_reason && profile.approval_status === 'rejected' && (
        <div data-testid="architect-rejection" style={{ padding: 12, background: '#FEF2F2',
          border: '1px solid #FECACA', borderRadius: theme.radius.sm, marginBottom: 14, color: '#991B1B', fontSize: 13 }}>
          <b>Rejected:</b> {profile.rejection_reason}
        </div>
      )}
      {profile?.admin_notes && (
        <div data-testid="architect-admin-notes" style={{ padding: 10,
          background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, marginBottom: 14, fontSize: 12 }}>
          <b>Admin note:</b> {profile.admin_notes}
        </div>
      )}

      {loading ? (
        <div data-testid="architect-profile-loading" style={{ padding: 24 }}>Loading…</div>
      ) : (
        <div style={cardStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Full Name *</label>
              <input data-testid="arch-full-name" style={inputStyle} value={form.full_name}
                onChange={e => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Phone</label>
              <input data-testid="arch-phone" style={inputStyle} value={form.phone || ''}
                onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Professional Role</label>
              <input data-testid="arch-professional-role" style={inputStyle} value={form.professional_role || ''}
                onChange={e => setForm({ ...form, professional_role: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Company / Firm</label>
              <input data-testid="arch-company" style={inputStyle} value={form.company_or_firm || ''}
                onChange={e => setForm({ ...form, company_or_firm: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>License Status (self-reported)</label>
              <select data-testid="arch-license-status" style={inputStyle}
                value={form.license_status} onChange={e => setForm({ ...form, license_status: e.target.value })}>
                {LICENSE_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Availability</label>
              <select data-testid="arch-availability" style={inputStyle}
                value={form.availability_status}
                onChange={e => setForm({ ...form, availability_status: e.target.value })}>
                {AVAILABILITY.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Website / Profile Link</label>
              <input data-testid="arch-website" style={inputStyle} value={form.website_or_profile_link || ''}
                onChange={e => setForm({ ...form, website_or_profile_link: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Specialties (self-reported until admin approval)</label>
            <div data-testid="arch-specialties" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {SPECIALTIES.map(s => {
                const on = form.specialties.includes(s);
                return (
                  <button key={s} type="button" data-testid={`arch-specialty-${s}`}
                    onClick={() => toggleSpecialty(s)}
                    style={{ padding: '5px 10px', borderRadius: 999, border: `1px solid ${on ? theme.colors.primary : theme.colors.borders}`,
                      background: on ? `${theme.colors.primary}18` : theme.colors.bg.card,
                      color: on ? theme.colors.primary : theme.colors.text.secondary,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>License Details</label>
            <textarea data-testid="arch-license-details" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              value={form.license_details || ''}
              onChange={e => setForm({ ...form, license_details: e.target.value })} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Credentials Summary</label>
            <textarea data-testid="arch-credentials" style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
              value={form.credentials_summary || ''}
              onChange={e => setForm({ ...form, credentials_summary: e.target.value })} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Experience Summary</label>
            <textarea data-testid="arch-experience" style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
              value={form.experience_summary || ''}
              onChange={e => setForm({ ...form, experience_summary: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button data-testid="arch-save-draft-btn" disabled={saving} onClick={() => save(false)}
              style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Save size={14} /> Save Draft
            </button>
            <button data-testid="arch-submit-btn" disabled={saving} onClick={() => save(true)}
              style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: 'none',
                background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Send size={14} /> Submit for Review
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
